import axios from "axios"
import { decode } from "msgpackr/unpack"
import { startTimer, now, record } from "@/service/performance"

const debug = 0

// The API edge-caches its own responses, so requests go straight to it - there
// is no caching proxy in front any more.
const API_URL = debug && "http://localhost:2000" || "https://api.leagueofpixels.eu"

const API_ENDPOINTS = {
  heroes: "heroes",
  items: "items",
  player: "player",
  playerNames: "player/names",
  match: "match",
  matches: "matches",
  leaderboard: "leaderboard",
}

// The names endpoint rejects anything larger with HTTP 400.
export const PLAYER_NAMES_BATCH_LIMIT = 200

const ASSET_BASE_URL = "https://leagueofpixels.eu/assets"

const getHeroAssetURL = (heroID) => { // Allow for hero_name to be passed in and converted to heroID
  return `${ASSET_BASE_URL}/creatures/heroes/HeroIcons/${heroID}/card.png`
}

const getItemAssetURL = (itemName) => {
  return `${ASSET_BASE_URL}/ui/game/items/${itemName}.png`
}

const getHeroAbilityAssetURL = (heroID, abilityID) => {
  return `${ASSET_BASE_URL}/creatures/heroes/HeroIcons/${heroID}/ability_${abilityID}.png`
}

const response_type_replay_buffer = { responseType: "arraybuffer" }

// msgpackr decodes any Uint8Array; the Buffer polyfill wrapper it used to get
// was an extra allocation and an extra dependency on the hot path.
const toBytes = (data) => {
  if (data instanceof Uint8Array) return data
  if (data instanceof ArrayBuffer) return new Uint8Array(data)
  if (data && data.buffer instanceof ArrayBuffer) return new Uint8Array(data.buffer, data.byteOffset || 0, data.byteLength)
  return new Uint8Array(0)
}

const decodeBytes = (data) => decode(toBytes(data))

const getBinary = async (url) => {
  const response = await axios.get(url, response_type_replay_buffer)
  return response.data
}

const getHeroesNames = async () => decodeBytes(await getBinary(`${API_URL}/${API_ENDPOINTS.heroes}`))

const getItems = async () => decodeBytes(await getBinary(`${API_URL}/${API_ENDPOINTS.items}`))

const getPlayer = async (player_ID_or_name) => {
  let identifier = `id=${player_ID_or_name}`
  if (isNaN(player_ID_or_name)) {
    identifier = `name=${player_ID_or_name}`
  }

  const data = await getBinary(`${API_URL}/${API_ENDPOINTS.player}?${identifier}`)
  if (!data.byteLength) {
    return {}
  }
  return decodeBytes(data)
}

const getMatch = async (matchID) => decodeBytes(await getBinary(`${API_URL}/${API_ENDPOINTS.match}?id=${matchID}`))

const buildMatchesQuery = (numberOfMatches, playerID, version, isRanked, offset) => {
  // Query strings are kept byte-for-byte identical to the previous
  // implementation so browser and edge HTTP caches keep hitting.
  if (playerID) {
    return `player=${playerID}` + (version && `&version=${version}` || "") + `&take=${numberOfMatches}` + (isRanked && `&ranked=1` || "") + `&offset=${offset}`
  }
  return (version && `version=${version}` || "") + `&take=${numberOfMatches}` + (isRanked && `&ranked=1` || "") + `&offset=${offset}`
}

/**
 * Fetches and decodes a matches page, reporting where the time went.
 *
 * `networkMs` covers the request until the whole body has arrived (axios
 * resolves after the arraybuffer is complete), `decodeMs` the synchronous
 * MessagePack decode. Both are also published as `kop:matches:network` /
 * `kop:matches:decode` User Timing measures.
 *
 * @returns {Promise<{ matches: object[], bytes: number, networkMs: number, decodeMs: number, url: string }>}
 */
const getMatchesDetailed = async (numberOfMatches, playerID = null, version = null, isRanked = false, offset = 0) => {
  const url = `${API_URL}/${API_ENDPOINTS.matches}?${buildMatchesQuery(numberOfMatches, playerID, version, isRanked, offset)}`
  const detail = { isRanked: Boolean(isRanked), take: numberOfMatches, playerID, version, offset }

  const networkStart = now()
  const data = await getBinary(url)
  const networkEnd = now()
  const bytes = data ? data.byteLength || 0 : 0
  record("matches:network", networkStart, networkEnd, { ...detail, bytes })

  const stopDecode = startTimer("matches:decode")
  const decoded = bytes ? decodeBytes(data) : []
  const matches = Array.isArray(decoded) ? decoded : []
  const decodeEntry = stopDecode({ ...detail, bytes, count: matches.length })

  return {
    matches,
    bytes,
    networkMs: networkEnd - networkStart,
    decodeMs: decodeEntry.duration,
    url,
  }
}

const getMatches = async (numberOfMatches, playerID = null, version = null, isRanked = false, offset = 0) => {
  const { matches } = await getMatchesDetailed(numberOfMatches, playerID, version, isRanked, offset)
  return matches
}

// Matches no longer carry player names, so they are resolved separately.
// Returns [{ id, name }]; ids with no matching account are omitted by the API.
const getPlayerNames = async (ids) => {
  if (!ids.length) {
    return []
  }

  if (ids.length > PLAYER_NAMES_BATCH_LIMIT) {
    throw new Error(`getPlayerNames accepts at most ${PLAYER_NAMES_BATCH_LIMIT} ids per request, got ${ids.length}`)
  }

  // Sorting keeps the query string stable for the same set of ids, so repeat
  // lookups hit any HTTP cache in front of us instead of missing on ordering.
  const sortedIds = [...ids].sort((a, b) => a - b)
  const data = await getBinary(`${API_URL}/${API_ENDPOINTS.playerNames}?ids=${sortedIds.join(",")}`)

  if (!data.byteLength) {
    return []
  }

  const bytes = toBytes(data)
  try {
    return decode(bytes)
  } catch (error) {
    // The API only emits msgpack in binary mode; fall back to JSON otherwise.
    return JSON.parse(new TextDecoder().decode(bytes))
  }
}

const getPlayerIDByName = async (playerName) => {
  const decoded = decodeBytes(await getBinary(`${API_URL}/${API_ENDPOINTS.player}?name=${playerName}`))
  return decoded.id
}

const getLeaderboard = async (heroStats = true, isRanked = false) => {
  heroStats = heroStats && 1 || 0
  const getQueryParams = `herostats=${heroStats}` + (isRanked && `&ranked=1` || "")
  return decodeBytes(await getBinary(`${API_URL}/${API_ENDPOINTS.leaderboard}?${getQueryParams}`))
}

export default {
  getHeroAssetURL,
  getItemAssetURL,
  getHeroAbilityAssetURL,
  getHeroesNames,
  getItems,
  getPlayer,
  getPlayerNames,
  getMatch,
  getMatches,
  getMatchesDetailed,
  getLeaderboard,
  getPlayerIDByName,
}
